import {
  SimpleForm,
  ReferenceInput,
  SelectInput,
  SaveButton,
  Toolbar,
  useTranslate,
} from "react-admin";
import { Dialog, DialogTitle, Button } from "@mui/material";
import {
  FieldValues,
  SubmitHandler,
  useFormContext,
  useWatch,
} from "react-hook-form";
import { appConfig } from "@/commons";
import { useEffect } from "react";

export function AssignLocationDialog({
  open,
  onClose,
  gamePseudo,
}: {
  open: boolean;
  onClose: () => void;
  gamePseudo: string | "";
}) {
  const translate = useTranslate();

  type AssignLocationForm = {
    pseudo: string;
    entity_id?: string | null;
    location_id?: string | null;
    cityId?: string | null;
  };

  const handleSubmit: SubmitHandler<AssignLocationForm> = async (values) => {
    const selectedGamePseudo = gamePseudo;

    const res = await fetch(`${appConfig.api.baseUrl}/game-session/selection`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      credentials: "include",
      body: JSON.stringify({
        entityId: values.entity_id,
        locationId: values.location_id,
      }),
    });

    if (!res.ok) {
      const text = await res.text();
      throw new Error(text || "Unable to save game selection");
    }

    const url = `${appConfig.app.gameUri}/${selectedGamePseudo.toLowerCase()}/`;

    window.location.href = url;
    onClose();
  };

  const defaultValues: Partial<AssignLocationForm> = { pseudo: gamePseudo };

  const onSubmit: SubmitHandler<FieldValues> = (raw) =>
    handleSubmit(raw as AssignLocationForm);

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>{translate("ra.gameoo.chooseLocation.title")}</DialogTitle>

      <SimpleForm
        onSubmit={onSubmit}
        defaultValues={defaultValues}
        toolbar={<DialogToolbar onCancel={onClose} />}
      >
        <ResetLocationOnCityChange />
        {/* Select Entity */}
        <ReferenceInput source="entity_id" reference="entities" perPage={50}>
          <SelectInput
            optionText="name"
            optionValue="id"
            label={translate("ra.gameoo.chooseLocation.entityLabel")}
          />
        </ReferenceInput>

        {/* Select City */}
        <CityInput />

        {/* Select Location filtered by city */}
        <LocationInput />
      </SimpleForm>
    </Dialog>
  );
}

function DialogToolbar({ onCancel }: { onCancel: () => void }) {
  const translate = useTranslate();
  return (
    <Toolbar>
      <Button onClick={onCancel}>
        {translate("ra.gameoo.chooseLocation.cancel")}
      </Button>
      <SaveButton label={translate("ra.gameoo.chooseLocation.save")} />
    </Toolbar>
  );
}

function CityInput() {
  const entityId = useWatch({ name: "entity_id" });
  const translate = useTranslate();

  return (
    <ReferenceInput
      source="cityId"
      reference="locations"
      filter={entityId ? { type: "city", entityId } : {}}
      perPage={100}
      disabled={!entityId}
    >
      <SelectInput
        optionText="name"
        optionValue="id"
        label={translate("ra.gameoo.chooseLocation.cityLabel")}
      />
    </ReferenceInput>
  );
}

function LocationInput() {
  const entityId = useWatch({ name: "entity_id" });
  const cityId = useWatch({ name: "cityId" });
  const translate = useTranslate();

  return (
    <ReferenceInput
      key={`${entityId ?? ""}-${cityId ?? ""}`}
      source="location_id"
      reference="locations"
      filter={entityId && cityId ? { entityId, cityId } : {}}
      perPage={100}
      sort={{ field: "name", order: "ASC" }}
      disabled={!entityId || !cityId}
    >
      <SelectInput
        optionText="name"
        optionValue="id"
        label={translate("ra.gameoo.chooseLocation.locationLabel")}
      />
    </ReferenceInput>
  );
}

function ResetLocationOnCityChange() {
  const cityId = useWatch({ name: "cityId" });
  const { setValue } = useFormContext();

  useEffect(() => {
    setValue("location_id", null, { shouldDirty: true, shouldValidate: true });
  }, [cityId, setValue]);

  return null;
}
